<template>
  <modal :open="internalOpen" :width="1000">
    <template #header>
      <modal-header variant="cross" :saving="loading" @cancel="closeModal()">
        <h1>{{ $t('pages.campaigns.waitingList.modal.new.title') }}</h1>
      </modal-header>
    </template>

    <form @submit="save">
      <div class="mt-8">
        <div class="flex flex-col w-full">
          <div class="flex flex-col gap-y-5">
            <Multiselect
              v-model="rawCampaignWaitingListRegistration.campaign"
              :error="errors?.campaign"
              class="col-start-1 col-end-2"
              :options="fetchCampaigns"
              :label="$t('campaignRegistration.campaign')"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
            />
            <Multiselect
              v-model="rawCampaignWaitingListRegistration.driver"
              :error="errors?.driver"
              class="col-start-1 col-end-2"
              :options="driverList"
              :label="$t('campaignRegistration.driver')"
              :can-deselect="false"
              :can-clear="false"
              :searchable="true"
              :loading="driverLoading"
            />
          </div>
          <div class="w-full text-center">
            <custom-button
              variant="primary"
              class="mt-15"
              :busy="loading"
              @click="save"
            >
              {{ $t('save') }}
            </custom-button>
          </div>
        </div>
      </div>
    </form>
  </modal>
</template>

<script lang="ts" setup>
import {
  computed, reactive, ref, unref,
} from 'vue';
import { useVModel } from '@vueuse/core';
import { useI18n } from 'vue-i18n';
import { useValidation } from '@hooks/form';
import { object, string } from 'yup';
import Modal from '@utils/Modal/Modal.vue';
import Multiselect from '@utils/Multiselect.vue';
import { useNotify } from '@utils/notifications';
import { useFetchAction } from '@hooks/fetch';
import ModalHeader from '@utils/Modal/ModalHeader.vue';
import CustomButton from '@utils/CustomButton.vue';
import { collOptionsProvider, useProvider } from '@utils/table/hooks/provider';
import type {
  Campaign, CampaignGroup, Driver, HydraCollection,
} from '@/entities/Entities';

interface Props {
  open: boolean;
  campaignGroup?: CampaignGroup;
}

const emit = defineEmits(['update:open', 'saved']);
const props = defineProps<Props>();
const internalOpen = useVModel(props, 'open', emit);

const { t } = useI18n();

const notify = useNotify();

const groupId = computed<{ group?: string }>(() =>
  (props.campaignGroup?.id ? { group: props.campaignGroup.id } : {}));

const params = reactive({
  filter: groupId,
});

const fetchCampaigns = collOptionsProvider('/campaigns', params, (value: Campaign) => ({
  label: value.name,
  value: value.id,
}));

const initialData = () => ({
  campaign: undefined,
  driver: undefined,
});

const rawCampaignWaitingListRegistration = ref({
  ...initialData(),
});

const campaignNeedsChoosing = computed(() => !rawCampaignWaitingListRegistration.value.campaign);

const driverFilter = computed<{ eligibleForCampaign?: string; pagination?: boolean }>(() =>
  (rawCampaignWaitingListRegistration.value?.campaign
    ? {
      eligibleForCampaignWaitingList: rawCampaignWaitingListRegistration.value.campaign,
      pagination: false,
    }
    : {}));

const driverParams = reactive({
  filter: driverFilter,
});

const { data: fetchDrivers, loading: driverLoading } = useProvider<HydraCollection<Driver>>(
  '/drivers',
  driverParams,
  { disabled: campaignNeedsChoosing },
);

const driverList = computed(() =>
  (!driverLoading.value
    ? unref(fetchDrivers)?.['hydra:member'].map((value: Driver) => ({
      label: value.user?.fullName,
      value: value['@id'],
    }))
    : []));

const {
  data: campaignWaitingListRegistration,
  errors,
  validatedFn,
  setValidation,
} = useValidation(
  object({
    campaign: string().nullable().required(t('validation.notEmpty')),
    driver: string().nullable().required(t('validation.notEmpty')),
  }),
  rawCampaignWaitingListRegistration,
);

function resetData() {
  rawCampaignWaitingListRegistration.value = {
    ...initialData(),
  } as unknown as typeof campaignWaitingListRegistration['value'];
  setValidation(false);
}

function closeModal() {
  resetData();
  internalOpen.value = false;
}

const [exec, { loading }] = useFetchAction();

const save = validatedFn(async () => {
  try {
    await exec('/campaign_waiting_list_registrations', {
      method: 'POST',
      body: {
        campaign: `/campaigns/${campaignWaitingListRegistration.value.campaign}`,
        driver: campaignWaitingListRegistration.value.driver,
      },
    });

    notify?.({
      variant: 'success',
      title: t('pages.campaigns.waitingList.modal.new.saveSuccess'),
    });

    emit('saved');
    closeModal();
  } catch (e) {
    notify?.({
      variant: 'error',
      title: t('pages.campaigns.waitingList.modal.new.saveError'),
    });
  }
});
</script>

<style lang="scss" scoped></style>
